import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { AppModules } from '@globals/types/AppModules';
import { HelloResponse } from '@globals/types/Hello';

/**
 * Flaga modyfikująca działanie aplikacji
 */
type flagType = ''
  | 'buyerViewNew'
  | 'showBuyerInAppReview'
  | 'branchIoQrLoginOnlyForOferteoEmail'
  | 'buyerPasswordSet'
  | 'buyerPhoneConfirmed'
  | 'showGoogleLogin'
  | 'autentiHideTrustedProfile'
  | 'autentiHideID'
  | 'the_best_award_season'
  | 'ap_promotional_campaign_enabled'
  | 'displayAutentiProfileMessage'
  | 'hideUncoverCounter'
  | 'accountRechargeRedesign'
  | 'showExpressInvitationBanner'
  | 'newMessagesSinceLastVisit'
  | 'enableAppStorePayments'
  | 'hasPromoPacket'
  | 'blackWeek'
  | 'debug'
  | 'hasMobileApp'
  | 'buyerLoadRfpDetailsNewWay'
  | 'showBannerTheBest'
  | 'displayTheBestSocialMediaScreen' // Ekran Najlepsi z możliwością pobrania plików na SM
  | 'reviewCustomersContestEnabled' // Konkurs na pozyskiwanie opinii 2025
  | 'phoneVerified'
  | 'inExtraMatching' // Czy jest dodany do ExtraMatchingu
;

export const useAppStore = defineStore('app', () => {
	const state = {
		module: ref(''),
		sessionId: ref(''),
		flags: ref<Array<string>>([]),
		baseUrl: ref(document.location.hostname), // Domain
		websocketUrl: ref(''), // WS
		googleTrackingId: ref('UA-2440395-3'), // UA
		googleAnalyticsData: ref({ userType: 'none' }), // Gtm
		fbTrackingId: ref(''), // Facebook
		userComTrackingId: ref(''), // User.com
		isVirtualKeyboardVisible: ref(false),
		isInApp: ref(false),
		pathname: ref(''),
		nextRouteNameAttempt: ref(''),
	};

	const actions = {
		update(rsvp: HelloResponse) {
			state.sessionId.value = rsvp.sessionId || '';
			state.baseUrl.value = rsvp.baseUrl || document.location.hostname;
			state.websocketUrl.value = rsvp.baseWebsocketUrl || '';
			state.googleTrackingId.value = rsvp.ua || 'UA-2440395-3';
			state.googleAnalyticsData.value = rsvp.analytics || { userType: 'none' };
			state.fbTrackingId.value = rsvp.facebook || '1439591673009945';
			state.userComTrackingId.value = rsvp.userComApiKey || '';

			actions.updateFlags(rsvp);
			state.isInApp.value = typeof wrapee !== 'undefined';
		},
		updateFlags(rsvp: HelloResponse) {
			const { user } = rsvp;
			state.flags.value = rsvp.flags || [];

			// Dodaj flagi do debugu z localStorage
			try {
				const customFlags = window.localStorage.getItem('flags');
				if (customFlags) {
					state.flags.value.push(...customFlags.split(',') || []);
				}
			} catch (err) {
				window.localStorage.setItem('flags', '');
			}

			if (rsvp.frontEndDebug) {
				rsvp.flags.push('frontEndDebug');
			}

			// CustomerApp
			if (user && user.idCustomer) {
				// Najlepsi 2023
				const awardsDeadline = window.localStorage['awardsDeadline_2023'] || '2023-05-11';
				window.localStorage['awardsDeadline_2023'] = awardsDeadline;
				if (Number(new Date()) < Number(new Date(awardsDeadline))) {
					state.flags.value.push('the_best_award_season');
				}

				// Czy pierwszą stroną mają być Kontakty, czy zapytania?
				if (user.newMessagesSinceLastVisit) {
					state.flags.value.push('newMessagesSinceLastVisit');
				}
			}

			// BuyerApp
			if (user) {
				if (user.passwordSet) {
					state.flags.value.push('buyerPasswordSet');
				}

				if (user.buyerViewNew) {
					state.flags.value.push('buyerViewNew');
				}

				if (user.phoneConfirmed) {
					state.flags.value.push('buyerPhoneConfirmed');
				}
			}
		},
		hasFlag(flag: flagType) {
			return state.flags.value.indexOf(flag) !== -1;
		},
		addFlag(flag: flagType) {
			if (!this.hasFlag(flag)) {
				state.flags.value.push(flag);
			}
		},
		removeFlag(flag: flagType) {
			const flagIndex = state.flags.value.findIndex((f: string) => f === flag);
			if (flagIndex >= 0) { state.flags.value.splice(flagIndex, 1); }
		},
	};

	const getters = {
		isAdminModule: computed(() => state.module.value === AppModules.ADMIN),
	};

	return { ...state, ...actions, ...getters };
});
